import React from 'react';
import { Info, ArrowUpRight, AlertTriangle } from 'lucide-react';

const TONES = {
  default: { icon: 'bg-muted text-slate-600', value: 'text-foreground' },
  primary: { icon: 'bg-primary/10 text-primary', value: 'text-foreground' },
  teal: { icon: 'bg-secondary/10 text-secondary', value: 'text-foreground' },
  success: { icon: 'bg-emerald-50 text-emerald-700', value: 'text-emerald-800' },
  warning: { icon: 'bg-amber-50 text-amber-700', value: 'text-amber-800' },
  danger: { icon: 'bg-rose-50 text-rose-700', value: 'text-rose-800' },
};

export const KpiCard = ({
  title, value, subtitle, icon: Icon, tone = 'default', highlight = false, warning = null,
  delta = null, tooltip = null, onClick = null, testId, className = '',
}) => {
  const t = TONES[tone] || TONES.default;
  const clickable = typeof onClick === 'function';
  return (
    <div
      data-testid={testId || `kpi-card-${String(title).toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
      onClick={clickable ? onClick : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => (e.key === 'Enter' || e.key === ' ') && onClick() : undefined}
      className={`relative overflow-hidden rounded-xl border bg-card p-5 shadow-xs hover-lift ${
        clickable ? 'cursor-pointer' : ''
      } ${highlight ? 'border-primary/40 ring-1 ring-primary/20' : 'border-border'} ${
        warning ? 'border-amber-300 bg-amber-50/40' : ''
      } ${className}`}
    >
      {highlight && <span className="absolute inset-x-0 top-0 h-1 bg-primary" />}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground truncate">{title}</p>
            {tooltip && (
              <span className="group relative inline-flex">
                <Info className="w-3.5 h-3.5 text-slate-400" />
                <span className="pointer-events-none absolute left-1/2 top-5 z-30 hidden w-64 -translate-x-1/2 rounded-lg border border-border bg-popover p-3 text-xs text-slate-600 shadow-lg group-hover:block">
                  {tooltip}
                </span>
              </span>
            )}
          </div>
          <p className={`font-display mt-1.5 text-2xl sm:text-3xl font-extrabold tracking-tight tabular-nums ${t.value}`}>{value}</p>
          {(subtitle || delta !== null) && (
            <div className="mt-1.5 flex items-center gap-2">
              {delta !== null && delta !== undefined && (
                <span
                  className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[11px] font-semibold ${
                    delta >= 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'
                  }`}
                >
                  {delta >= 0 ? '+' : ''}{delta}%
                </span>
              )}
              {subtitle && <p className="text-xs text-muted-foreground truncate">{subtitle}</p>}
            </div>
          )}
          {warning && (
            <p className="mt-2 inline-flex items-center gap-1 rounded-md bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800">
              <AlertTriangle className="w-3 h-3" /> {warning}
            </p>
          )}
        </div>
        <div className="flex flex-col items-end gap-2">
          {Icon && (
            <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${t.icon}`}>
              <Icon className="h-5 w-5" />
            </div>
          )}
          {clickable && <ArrowUpRight className="h-4 w-4 text-slate-300" />}
        </div>
      </div>
    </div>
  );
};
